import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { useParams, Link } from "react-router-dom";

// ✅ ONE canonical slug generator (must match list page)
const generateSlug = (title) =>
  title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");


// ✅ Excerpt data
export const excerptSummaries = [
  {
    id: generateSlug("Sing to me of the man, Muse, the man of twists and turns…"),
    title: "Sing to me of the man, Muse, the man of twists and turns…",
    imgSrc: "../media/nib.jpg",
    alt: "Homer portrait",
    author: "Homer",
    referance: "The Odyssey",
    published: "8th century BC",
    Language: "Ancient Greek",

    excerptOne: "This excerpt is used at the very beginning of the epic, in the opening invocation to the Muse. It sets the stage for the entire story of Odysseus, highlighting his cunning nature and the long, arduous journey he faces. By calling upon divine inspiration, Homer introduces readers to a tale of heroism, endurance, and human struggle. This opening also frames the epic as both a personal story of Odysseus and a universal exploration of human resilience, immediately engaging the audience and establishing the importance of narrative tradition in ancient Greek culture.",
    excerptTwo: "This line emphasizes the challenges and experiences that shape Odysseus as he travels. It impacts the story by illustrating the wide-ranging trials he must endure, from natural disasters to encounters with foreign peoples. By recounting these hardships, the narrative demonstrates how knowledge, patience, and intelligence are cultivated through suffering. This passage deepens the tension and complexity of the story, showing that heroism is not only about physical strength but also about endurance, strategic thinking, and moral courage in the face of adversity.",
    excerptThree: "Culturally, this excerpt reflects Greek values such as loyalty, courage, and respect for divine authority. It highlights the tension between human effort and fate, a recurring theme in Greek literature. The text emphasizes that individuals must balance personal initiative with reverence for higher powers, and that reckless actions can have tragic consequences. By showing the consequences of choices and the inevitability of certain outcomes, it reinforces lessons that were central to ancient Greek society, guiding moral conduct and shaping ideals of leadership, heroism, and social responsibility.",
    excerptFour: "Historically, this line illustrates the long-lasting influence of Greek mythology and epic storytelling. Tales like that of Odysseus served as a foundation for Western literature, philosophy, and moral thought. The narrative of divine punishment for human error became a moral framework for generations, demonstrating how ancient storytelling preserved cultural values and ethical teachings. This passage also highlights the educational role of epics, as they provided examples of behavior, consequences, and societal norms that informed historical understanding and literary development across centuries.",
    excerptFive: "For the reader, this excerpt invites engagement with a world of adventure, suspense, and moral reflection. It encourages contemplation of human resilience, identity, and the pursuit of home despite hardship. By framing the story as divinely inspired, Homer reinforces the importance of storytelling as a shared human experience that educates, entertains, and inspires. Readers are prompted to empathize with Odysseus, reflect on their own challenges, and consider broader philosophical questions about human nature, fate, and the ethical dimensions of personal decisions.",

    majorWorks: ["Iliad", "Odyssey"],
    themes: ["Heroism", "Journey", "Fate"],
    crossCulture: [
      "Inspired Roman epics",
      "Foundation of Western storytelling",
    ],
  },

  {
    id: generateSlug("Midway upon the journey of our life, I found myself within a forest dark…"),
    title: "Midway upon the journey of our life, I found myself within a forest dark…",
    imgSrc: "../media/nib.jpg",
    alt: "Dante portrait",
    author: "Dante Alighieri",
    referance: "The Divine Comedy",
    published: "14th century",
    Language: "Italian",

    excerptOne: "This excerpt is from the opening lines of The Divine Comedy, where Dante finds himself lost in a dark wood, symbolizing moral confusion and spiritual crisis. It is used at the very beginning of the poem to set the stage for a journey of self-discovery, redemption, and enlightenment. By introducing Dante’s confusion and fear, the text immediately establishes the epic’s central themes: the struggle between sin and virtue, the journey of the soul, and the importance of guidance, both divine and human, in overcoming life’s challenges",
    excerptTwo: "This part of the poem emphasizes Dante’s encounters with sinners, virtuous souls, and allegorical figures as he travels through Hell, Purgatory, and Paradise. It impacts the story by structuring the narrative around moral lessons and spiritual growth, showing that the journey is both literal and symbolic. Each encounter challenges Dante’s understanding, compelling him to reflect on human behavior, divine justice, and the consequences of choices, thereby reinforcing the narrative’s layered complexity and philosophical depth",
    excerptThree: "Culturally, The Divine Comedy reflects medieval Italian and broader European values, combining Christian theology with classical philosophy. It embodies ideals such as justice, morality, and human responsibility, while presenting a vivid allegorical framework to explore ethical questions. By integrating historical figures, mythological references, and contemporary Italian society, the text preserves and communicates cultural knowledge, making the poem not only a literary masterpiece but also a mirror of the beliefs, fears, and hopes of its time.",
    excerptFour: "Historically, The Divine Comedy has had an immense impact on literature, theology, and the arts. Written in the early 14th century, it influenced countless writers, painters, and thinkers throughout Europe, shaping the Western literary canon. Its use of vernacular Italian helped elevate the language as a literary medium, inspiring national identity and pride. Additionally, its allegorical treatment of human morality, politics, and divine law has served as a foundation for moral and philosophical education for centuries, demonstrating the enduring power of storytelling in shaping intellectual history.",
    excerptFive: "For the reader, this excerpt invites reflection on human nature, morality, and the soul’s journey toward enlightenment. The vivid imagery and symbolic structure engage the imagination while encouraging contemplation of ethical choices, spiritual growth, and personal responsibility. By witnessing Dante’s struggles and triumphs, readers gain insights into universal themes of fear, hope, and redemption. The poem’s depth and complexity make it both challenging and rewarding, offering enduring lessons about life, morality, and the pursuit of higher understanding.",

    majorWorks: ["The Divine Comedy"],
    themes: ["Redemption", "Sin", "Divine Justice"],
    crossCulture: [
      "Foundation of Italian literature",
      "Influenced Western theology and poetry"
    ],
  },

  {
    id: generateSlug("The truth may be stretched thin, but it never breaks…"),
    title: "The truth may be stretched thin, but it never breaks…",
    imgSrc: "../media/nib.jpg",
    alt: "Miguel de Cervantes portrait",
    author: "Miguel de Cervantes",
    referance: "Don Quixote",
    published: "1605-1615",
    Language: "Spanish",

    excerptOne: "At the start of Don Quixote, the protagonist is introduced as an aging man obsessed with chivalric romances. This moment establishes the story’s theme of illusion versus reality, setting the stage for both comedy and philosophical reflection. It shows how literature can shape perception and action, illustrating the power of imagination and the dangers of letting fantasy overtake reason.",
    excerptTwo: "The narrative impact is profound, as Don Quixote’s delusions drive the plot and interactions with others. His idealism contrasts with the practical world, creating a tension that allows readers to examine the nature of heroism, folly, and human ambition. This conflict gives the story depth beyond humor, making it a meditation on life, values, and the pursuit of meaning.",
    excerptThree: "Culturally, the book reflects 17th-century Spanish society, highlighting social hierarchies, rural life, and literary traditions. Cervantes critiques rigid norms and exposes contradictions between ideals and lived reality. Through satire, it preserves knowledge about Spanish culture and moral thought, teaching lessons about humility, compassion, and critical thinking.",
    excerptFour: "Historically, Don Quixote is considered the first modern novel, influencing countless writers and shaping narrative forms worldwide. Its mix of realism, metafiction, and social commentary set precedents in storytelling. Studying it helps readers understand the evolution of literature and the ways fiction can examine human psychology, ethics, and social structures.",
    excerptFive: "For readers, the story is both entertaining and intellectually stimulating. It encourages self-reflection on personal ideals, perseverance, and adaptability in the face of reality. Observing Don Quixote’s triumphs and failures teaches empathy, critical observation, and appreciation for the interplay between imagination and practical wisdom.",

    majorWorks: ["Don Quixote"],
    themes: ["Reality vs Illusion", "Idealism", "Madness"],
    crossCulture: [
      "Birth of the modern novel",
      "Influenced European narrative fiction"
    ],
  },

  {
    id: generateSlug("Where the mind is without fear and the head is held high…"),
    title: "Where the mind is without fear and the head is held high…",
    imgSrc: "../media/nib.jpg",
    alt: "Rabindranath Tagore portrait",
    author: "Rabindranath Tagore",
    referance: "Gitanjali",
    published: "1910",
    Language: "Bengali",

    excerptOne: "Rabindranath Tagore’s Gitanjali opens with intimate, devotional verses that express longing for the divine. This excerpt sets the tone of spiritual reflection and personal introspection, showing how poetry can serve as a bridge between human emotion and higher understanding. It immerses the reader in contemplative beauty and philosophical depth.",
    excerptTwo: "The poem’s impact lies in its universality: themes of love, mortality, and devotion resonate across cultures. Each verse invites meditation, allowing the narrative to unfold emotionally rather than linearly. The lyrical structure fosters a rhythm of reflection that emphasizes the interconnectedness of life, nature, and spirituality.",
    excerptThree: "Culturally, Gitanjali preserves Bengali religious and poetic traditions while introducing them to a global audience. Its devotional tone reflects India’s spiritual ethos and the philosophical depth of Bhakti literature. It also demonstrates how cultural expression can transcend language, encouraging cross-cultural understanding.",
    excerptFour: "Historically, the collection won Tagore the Nobel Prize in Literature, making it a milestone in world literature. It marked the first major recognition of Indian writing in English translation, influencing poets and thinkers worldwide. Its spiritual and lyrical qualities helped shape modern poetic forms and global appreciation of Indian literature.",
    excerptFive: "For readers, the poems evoke introspection and a heightened awareness of life’s fleeting beauty. They inspire meditation on personal purpose, devotion, and human connection. Gitanjali demonstrates that poetry can educate emotionally and morally, cultivating both aesthetic appreciation and spiritual sensitivity.",

    majorWorks: ["Gitanjali"],
    themes: ["Freedom", "Spirituality", "National Identity"],
    crossCulture: [
      "First Asian Nobel laureate in Literature",
      "Bridged Eastern and Western thought"
    ],
  },

  {
    id: generateSlug("Pain and suffering are always inevitable for a large intelligence and a deep heart."),
    title: "Pain and suffering are always inevitable for a large intelligence and a deep heart.",
    imgSrc: "../media/nib.jpg",
    alt: "Fyodor Dostoevsky portrait",
    author: "Fyodor Dostoevsky",
    referance: "Crime and Punishment",
    published: "1880",
    Language: "Russian",

    excerptOne: "The opening of Dostoevsky’s Crime and Punishment introduces Raskolnikov in psychological turmoil, setting the stage for a study of morality, guilt, and existential struggle. The excerpt establishes the protagonist’s internal conflict and the philosophical themes that dominate the narrative, offering insight into the human psyche under extreme moral pressure.",
    excerptTwo: "The story’s impact is profound: Raskolnikov’s crime and subsequent suffering drive the plot, revealing consequences of ethical transgression. Each decision deepens tension and forces the reader to confront questions about justice, redemption, and moral relativism. This exploration makes the narrative a psychological and philosophical investigation.",
    excerptThree: "Culturally, the novel reflects 19th-century Russian society, its inequalities, and intellectual debates. Dostoevsky’s depiction of poverty, social expectations, and ideological movements provides insight into the cultural forces shaping individual behavior, moral philosophy, and societal structures.",
    excerptFour: "Historically, the novel influenced existentialism, psychology, and literature worldwide. It introduced complex character studies and moral dilemmas that challenged readers to reflect critically on human nature, law, and ethics. Its themes continue to inform philosophical discourse and literary studies.",
    excerptFive: "For readers, the work is a journey into ethical reasoning, empathy, and self-examination. It cultivates moral awareness, emotional intelligence, and the ability to confront uncomfortable truths about human behavior. Engaging with the text strengthens critical thinking and ethical reflection.",

    majorWorks: ["Crime and Punishment"],
    themes: ["Guilt", "Redemption", "Morality"],
    crossCulture: [
      "Influenced existential philosophy",
      "Shaped psychological fiction"
    ],
  },

  {
    id: generateSlug("Many years later, as he faced the firing squad, Colonel Aureliano Buendía was to remember…"),
    title: "Many years later, as he faced the firing squad, Colonel Aureliano Buendía was to remember…",
    imgSrc: "../media/nib.jpg",
    alt: "Gabriel García Márquez portrait",
    author: "Gabriel García Márquez",
    referance: "One Hundred Years of Solitude",
    published: "1967",
    Language: "Spanish",

    excerptOne: "The opening of García Márquez’s One Hundred Years of Solitude introduces the town of Macondo and the Buendía family, blending historical reality with magical events. This establishes the novel’s signature style of magical realism, where extraordinary phenomena are treated as ordinary, inviting readers to explore memory, time, and human legacy in imaginative ways.",
    excerptTwo: "The story’s impact lies in its intricate narrative and cyclical structure, showing how history, destiny, and family dynamics shape human experience. By weaving multiple generations into one epic narrative, the novel examines repetition, consequences, and the persistence of cultural memory, providing a rich exploration of cause and effect across time.",
    excerptThree: "Culturally, the novel preserves Latin American history, folklore, and traditions through its imaginative lens. It highlights societal issues like colonialism, political unrest, and social change, reflecting regional identity while demonstrating the power of storytelling to encode communal memory and shared values.",
    excerptFour: "Historically, the book revolutionized literature by popularizing magical realism, influencing writers worldwide. It offered a new way to narrate history and human experience, blending fantasy and realism to create allegorical depth. Its impact continues in contemporary literature, cinema, and cultural studies.",
    excerptFive: "For readers, the novel inspires reflection on memory, identity, and the passage of time. It challenges conventional narrative expectations while fostering empathy and critical awareness of human behavior. The story demonstrates the potential of literature to illuminate social, cultural, and philosophical truths through imaginative storytelling.",

    majorWorks: ["One Hundred Years of Solitude"],
    themes: ["Magic Realism", "Time", "Solitude"],
    crossCulture: [
      "Defined Latin American Boom",
      "Globalized magical realism"
    ],
  },

  {
    id: generateSlug("The white man is very clever. He came quietly and peaceably with his religion…"),
    title: "The white man is very clever. He came quietly and peaceably with his religion…",
    imgSrc: "../media/nib.jpg",
    alt: "Chinua Achebe portrait",
    author: "Chinua Achebe",
    referance: "Things Fall Apart",
    published: "1958",
    Language: "English",

    excerptOne: "Achebe’s Things Fall Apart opens with Okonkwo’s life in the Igbo village of Umuofia, introducing the reader to pre-colonial African society. This excerpt establishes the novel’s themes of tradition, change, and individual ambition, showing how personal choices intersect with communal values and societal norms.",
    excerptTwo: "The story’s impact is in depicting the collision of cultures and the tension between tradition and colonial influence. Okonkwo’s personal struggles mirror the larger upheaval affecting his society, creating a narrative that explores the fragility of social structures and the consequences of resistance or adaptation.",
    excerptThree: "Culturally, the novel preserves Igbo customs, rituals, and governance, offering insight into African history and worldview. It challenges Western stereotypes by presenting African life with depth and nuance, fostering understanding and appreciation of diverse cultural frameworks.",
    excerptFour: "Historically, the book is foundational in postcolonial literature, influencing debates on cultural representation and identity. It documented African experiences during colonial encroachment, inspiring generations of writers to explore indigenous perspectives and the impacts of globalization and imperialism.",
    excerptFive: "For readers, the novel provides lessons on resilience, cultural pride, and the complexities of human behavior within societal systems. It encourages critical thought about the forces shaping communities and individual choices, and highlights the value of storytelling in preserving cultural memory.",

    majorWorks: ["Things Fall Apart"],
    themes: ["Colonialism", "Tradition", "Cultural Conflict"],
    crossCulture: [
      "Foundational African novel in English",
      "Reframed colonial narratives"
    ],
  },

  {
    id: generateSlug("Memory is a funny thing. When I was in college, I spent a lot of time thinking about death."),
    title: "Memory is a funny thing. When I was in college, I spent a lot of time thinking about death.",
    imgSrc: "../media/nib.jpg",
    alt: "Haruki Murakami portrait",
    author: "Haruki Murakami",
    referance: "Norwegian Wood",
    published: "1987",
    Language: "Japanese",

    excerptOne: "Murakami’s Norwegian Wood opens with Toru Watanabe reflecting on his youth and the suicide of a friend, immediately establishing themes of memory, loss, and emotional fragility. This excerpt frames the story as a contemplative journey through grief and coming-of-age experiences, emphasizing the intimate emotional landscape of the protagonist.",
    excerptTwo: "The narrative impact lies in the exploration of relationships, mental health, and existential uncertainty. Toru’s interactions with friends and lovers reveal the tension between desire, obligation, and personal healing, highlighting how individual experiences shape identity and understanding of the world.",
    excerptThree: "Culturally, the novel reflects 1960s Japan, including social norms, student movements, and urban life. It captures generational anxieties and the influence of Western culture, providing a lens into Japanese society’s evolving values and youth culture during a period of social transformation.",
    excerptFour: "Historically, the work is significant for introducing modern Japanese literature to a global audience. Murakami’s style, blending realism with surreal and emotional depth, influenced contemporary fiction worldwide, shaping literary trends and cross-cultural understanding.",
    excerptFive: "For readers, the novel fosters empathy, emotional intelligence, and reflection on loss, love, and human connection. It encourages introspection on personal choices and the universality of grief, demonstrating literature’s power to engage both heart and mind in understanding the human experience.",

    majorWorks: ["Norwegian Wood"],
    themes: ["Memory", "Love", "Loss"],
    crossCulture: [
      "Globalized contemporary Japanese fiction",
      "Blended Western and Japanese culture"
    ],
  },

  {
    id: generateSlug("For nothing was simply one thing. The other Lighthouse was true too."),
    title: "For nothing was simply one thing. The other Lighthouse was true too.",
    imgSrc: "../media/nib.jpg",
    alt: "Virginia Woolf portrait",
    author: "Virginia Woolf",
    referance: "To the Lighthouse",
    published: "1927",
    Language: "English",

    excerptOne: "Woolf’s To the Lighthouse opens with the Ramsay family’s anticipation of a visit to the lighthouse, establishing themes of time, perception, and domestic life. The excerpt highlights the interplay of consciousness and memory, signaling the novel’s focus on inner experiences rather than external action.",
    excerptTwo: "The story’s impact lies in its exploration of human relationships, impermanence, and subjective reality. The narrative’s shifting perspectives and stream-of-consciousness technique allow readers to experience the characters’ thoughts intimately, providing deep insight into emotional and intellectual life.",
    excerptThree: "Culturally, the novel reflects early 20th-century British social norms, gender roles, and artistic aspirations. Woolf challenges traditional storytelling and explores feminist perspectives, offering commentary on the evolving roles of women and the significance of creative expression.",
    excerptFour: "Historically, the book is a landmark of modernist literature, influencing narrative form and psychological realism. Its experimentation with time, perspective, and symbolism reshaped literary expression, contributing to broader debates on consciousness, narrative structure, and literary innovation.",
    excerptFive: "For readers, the work cultivates sensitivity to subtle human emotions, perception, and philosophical reflection on life and mortality. It encourages mindfulness of temporal and relational experiences, demonstrating how literature can illuminate the depths of ordinary existence with extraordinary insight.",

    majorWorks: ["To the Lighthouse"],
    themes: ["Time", "Perception", "Consciousness"],
    crossCulture: [
      "Pioneered stream-of-consciousness",
      "Central to modernist literature"
    ],
  },

  {
    id: generateSlug("Mother died today. Or, maybe, yesterday; I can’t be sure."),
    title: "Mother died today. Or, maybe, yesterday; I can’t be sure.",
    imgSrc: "../media/nib.jpg",
    alt: "Albert Camus portrait",
    author: "Albert Camus",
    referance: "The Stranger",
    published: "1942",
    Language: "French",

    excerptOne: "Camus’ The Stranger opens with Meursault learning of his mother’s death, immediately establishing themes of alienation, absurdity, and detachment. This excerpt frames the existential inquiry at the heart of the novel, questioning the meaning of life, societal expectations, and the human response to mortality.",
    excerptTwo: "The story’s impact is significant in highlighting the consequences of emotional detachment and moral indifference. Meursault’s experiences reveal society’s insistence on conformity and the tension between personal truth and communal judgment, making the narrative a profound reflection on human existence.",
    excerptThree: "Culturally, the novel illustrates postcolonial Algerian-French society while exploring universal themes of isolation, absurdity, and ethical ambiguity. It engages readers in moral and philosophical reflection, challenging conventional perspectives on justice, emotion, and social norms.",
    excerptFour: "Historically, The Stranger is a cornerstone of existentialist literature, influencing philosophy, literature, and psychology. Camus’ exploration of the absurd shaped intellectual discourse on human freedom, responsibility, and the search for meaning in an indifferent world.",
    excerptFive: "For readers, the work encourages contemplation of life, mortality, and authenticity. It challenges assumptions about social expectations, morality, and the nature of happiness, fostering critical thinking and philosophical curiosity about human behavior and existential questions.",

    majorWorks: ["The Stranger"],
    themes: ["Absurdism", "Alienation", "Existence"],
    crossCulture: [
      "Defined absurdist fiction",
      "Influenced existential philosophy"
    ],
  },

  {
    id: generateSlug("I am nothing but a corpse now, a body at the bottom of a well."),
    title: "I am nothing but a corpse now, a body at the bottom of a well.",
    imgSrc: "../media/nib.jpg",
    alt: "Orhan Pamuk portrait",
    author: "Orhan Pamuk",
    referance: "My Name Is Red",
    published: "1998",
    Language: "Turkish",

    excerptOne: "Pamuk’s My Name is Red opens with the murder of a miniaturist in the Ottoman Empire, immediately immersing the reader in a world of art, intrigue, and conflicting values. This excerpt sets the stage for a story that blends mystery with philosophical reflection on art, identity, and perception, emphasizing how creativity can challenge tradition and provoke conflict.",
    excerptTwo: "The story’s impact is in exploring the tension between individual expression and societal expectations. The mystery drives the narrative while raising questions about artistic freedom, religious constraints, and the nature of beauty, making the novel both intellectually engaging and narratively compelling.",
    excerptThree: "Culturally, the novel preserves Ottoman artistic traditions and Islamic aesthetics, illuminating historical methods of miniature painting and cultural attitudes toward art. It provides readers with insight into the intersection of art, religion, and social norms, revealing the philosophical and ethical dimensions of creative work.",
    excerptFour: "Historically, the book has contributed to global understanding of Ottoman culture and influenced discussions on the universality of art and human creativity. Pamuk’s narrative demonstrates how literature can educate readers about history, philosophy, and the enduring significance of artistic heritage.",
    excerptFive: "For readers, the novel encourages appreciation of aesthetic complexity, ethical reflection, and the subtleties of human motivation. It invites contemplation on identity, morality, and the power of storytelling, cultivating both artistic and intellectual curiosity.",

    majorWorks: ["My Name Is Red"],
    themes: ["Art", "Identity", "East vs West"],
    crossCulture: [
      "Explores Ottoman art tradition",
      "Bridges Eastern and Western narrative forms"
    ],
  },

  {
    id: generateSlug("124 was spiteful. Full of a baby’s"),
    title: "124 was spiteful. Full of a baby’s",
    imgSrc: "../media/nib.jpg",
    alt: "Toni Morrison portrait",
    author: "Toni Morrison",
    referance: "Beloved",
    published: "1987",
    Language: "English",

    excerptOne: "Morrison’s Beloved opens with Sethe’s haunted house and the presence of her deceased daughter, immediately establishing themes of memory, trauma, and the legacy of slavery. This excerpt frames the novel as a meditation on historical injustice and personal suffering, emphasizing the enduring psychological effects of oppression.",
    excerptTwo: "The story’s impact lies in its unflinching exploration of slavery’s consequences on individuals and families. The narrative intertwines past and present, showing how trauma shapes identity, relationships, and perception, creating a deeply moving and morally resonant account.",
    excerptThree: "Culturally, the novel preserves African American history and oral traditions, exploring the ways communities remember and transmit collective trauma. Morrison presents nuanced portrayals of survival, resilience, and moral complexity, offering insights into cultural memory and social justice.",
    excerptFour: "Historically, Beloved brought renewed attention to African American literature and the historical realities of slavery, influencing academic and social discourse. It contributes to understanding systemic oppression, memory, and the importance of acknowledging marginalized voices in shaping history and literature.",
    excerptFive: "For readers, the novel provokes empathy, self-reflection, and engagement with ethical and emotional complexities. It demonstrates the power of literature to illuminate painful histories, foster moral awareness, and encourage critical thought about human rights, memory, and justice.",

    majorWorks: ["Beloved"],
    themes: ["Slavery", "Memory", "Haunting"],
    crossCulture: [
      "Central to African American literature",
      "Reframed historical trauma in fiction"
    ],
  },
];

export const ExcerptsSummary = () => {
  const { id } = useParams();
  const decodedId = decodeURIComponent(id);

  const excerpt = excerptSummaries.find(
    (item) => item.id === decodedId
  );

  useEffect(() => {
    AOS.init({
      duration: 900,
      easing: "ease-out-cubic",
      once: false,
    });
  }, []);

  // ✅ Graceful fallback (NO blank screen)
  if (!excerpt) {
    return (
      <div style={{ padding: "5rem 1rem", textAlign: "center" }}>
        <h2>Excerpt Not Found</h2>
        <p>The excerpt you are looking for does not exist.</p>
        <Link
          to="/excerpts"
          style={{
            color: "#000",
            textDecoration: "none",
            fontWeight: 500,
            backgroundColor: "#fff",
            padding: "0.4rem 1rem",
            borderRadius: "999px",
            border: "1px solid #ddd",
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          }}
        >
          ← Back to Excerpts
        </Link>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Header */}
      <div className="row mt-4 pt-4 align-items-start">
        {/* Left column */}

        <div className="row mb-4">
          {/* <img
            src={excerpt.imgSrc}
            alt={excerpt.alt}
            className="img-fluid mb-3"
            style={{
              maxHeight: "300px",
              objectFit: "contain",
              borderRadius: "1rem",
            }}
          /> */}

          <h1 className="fs-5 text-center py-4" style={{fontWeight: 500,
            backgroundColor: "#fff",
            padding: "0.5rem 1.2rem",
            borderRadius: "999px",
            border: "1px solid #ddd",
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",}}>{excerpt.title}</h1>
          
        </div>

        {/* Right column */}
        <div
            className="col-12 col-md-3 text-center text-md-start mb-4 "
            data-aos="zoom-in"
          >

            <div className="row text-center">

              <h3 className="mt-4 mb-2" data-aos="fade-left" data-aos-delay="300">Author</h3>
              <p className="lh-lg" data-aos="fade-up" data-aos-delay="400">{excerpt.author}</p>

              <h3 className="mt-4 mb-2" data-aos="fade-left" data-aos-delay="500">Referance</h3>
              <p className="lh-lg" data-aos="fade-up" data-aos-delay="600">{excerpt.referance}</p>

              <h3 className="mt-4 mb-2" data-aos="fade-left" data-aos-delay="700">Published</h3>
              <p className="lh-lg" data-aos="fade-up" data-aos-delay="800">{excerpt.published}</p>

              <h3 className="mt-4 mb-2" data-aos="fade-left" data-aos-delay="900">Language</h3>
              <p className="lh-lg" data-aos="fade-up" data-aos-delay="1000">{excerpt.Language}</p>
            </div>
          </div>
        <div className="col-12 col-md-9 px-4">
          <h3 className="fw-semibold mb-3" data-aos="fade-left">
            Importance of this Excerpt
          </h3>

          <p className="lh-lg" data-aos="fade-up" data-aos-delay="100">
            {excerpt.excerptOne}
          </p>
          <p className="lh-lg" data-aos="fade-up" data-aos-delay="200">
            {excerpt.excerptTwo}
          </p>
          <p className="lh-lg" data-aos="fade-up" data-aos-delay="300">
            {excerpt.excerptThree}
          </p>
          <p className="lh-lg" data-aos="fade-up" data-aos-delay="400">
            {excerpt.excerptFour}
          </p>
          <p className="lh-lg" data-aos="fade-up" data-aos-delay="500">
            {excerpt.excerptFive}
          </p>
        </div>
      </div>

      {/* Info Cards */}
      <div className="row mt-4 gy-4">
        <div className="col-12 col-md-3" data-aos="zoom-in">
          <div className="text-center p-4 h-100 shadow-sm rounded-4">
            <h5 className="fw-semibold mb-2">Major Works</h5>
            <p className="text-secondary mb-0">
              {excerpt.majorWorks.join(", ")}.
            </p>
          </div>
        </div>

        <div
          className="col-12 col-md-3"
          data-aos="zoom-in"
          data-aos-delay="100"
        >
          <div className="text-center p-4 h-100 shadow-sm rounded-4">
            <h5 className="fw-semibold mb-2">Themes</h5>
            <p className="text-secondary mb-0">
              {excerpt.themes.join(", ")}.
            </p>
          </div>
        </div>

        <div
          className="col-12 col-md-6"
          data-aos="zoom-in"
          data-aos-delay="200"
        >
          <div className="text-center p-4 h-100 shadow-sm rounded-4">
            <h5 className="fw-semibold mb-2">Cultural Influence</h5>
            <p className="text-secondary mb-0">
              {excerpt.crossCulture.join(", ")}.
            </p>
          </div>
        </div>
      </div>

      {/* Back button */}
      <div
        className="d-flex justify-content-center mt-4 mb-4"
        data-aos="zoom-in-up"
      >
        <Link
          to="/excerpts"
          style={{
            color: "#000",
            textDecoration: "none",
            fontWeight: 500,
            backgroundColor: "#fff",
            padding: "0.5rem 1.2rem",
            borderRadius: "999px",
            border: "1px solid #ddd",
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          }}
        >
          ← Back to Excerpts
        </Link>
      </div>
    </div>
  );
};
